/**
 * Lightweight browser crash reporter → SigNoz via OTLP/HTTP traces.
 * Captures window.onerror, unhandledrejection, and explicit reportError() calls.
 */

const DEFAULT_ENDPOINT_KEY = 'REACT_APP_OTEL_ENDPOINT'
const DEFAULT_SERVICE_KEY = 'REACT_APP_OTEL_SERVICE_NAME'
const DEFAULT_ENV_KEY = 'REACT_APP_OTEL_ENVIRONMENT'
const DEFAULT_ENABLED_KEY = 'REACT_APP_OTEL_ENABLED'

const getEnv = (key) => {
  if (typeof window === 'undefined' || !window._env_) {
    return undefined
  }
  return window._env_[key]
}

const isEnabled = () => {
  const flag = `${getEnv(DEFAULT_ENABLED_KEY) || 'true'}`.toLowerCase()
  if (flag === 'false' || flag === '0' || flag === 'off') {
    return false
  }
  return Boolean(getEnv(DEFAULT_ENDPOINT_KEY))
}

const randomHex = (bytes) => {
  const arr = new Uint8Array(bytes)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr)
  } else {
    for (let i = 0; i < bytes; i += 1) {
      arr[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

const attr = (key, value) => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  return { key, value: { stringValue: String(value) } }
}

const safeJson = (value) => {
  try {
    return JSON.stringify(value)
  } catch (_) {
    return null
  }
}

/** Turn Promise rejection reasons (incl. API `{ status, message, error }` objects) into Errors. */
export const normalizeRejectionReason = (reason) => {
  if (reason instanceof Error) {
    return reason
  }
  if (reason == null) {
    return new Error('unhandledrejection')
  }
  if (typeof reason === 'string') {
    return new Error(reason)
  }
  if (typeof reason === 'object') {
    const nested = reason.error
    const message =
      (typeof reason.message === 'string' && reason.message) ||
      (nested && typeof nested.message === 'string' && nested.message) ||
      (reason.status != null ? `Request failed with status ${reason.status}` : null) ||
      safeJson(reason) ||
      'unhandledrejection'
    const err = new Error(message)
    if (reason.status != null) {
      err.status = reason.status
    }
    err.detail = safeJson(reason) || undefined
    return err
  }
  return new Error(String(reason))
}

const buildPayload = ({ error, source, componentStack }) => {
  // Nanoseconds as decimal string (avoid float). Non-zero duration helps SigNoz list views.
  const startMs = Date.now()
  const startNano = `${startMs}000000`
  const endNano = `${startMs + 1}000000`
  const message = error && error.message ? error.message : String(error || 'Unknown error')
  const name = (error && error.name) || 'Error'
  const stack = (error && error.stack) || componentStack || ''

  const resourceAttrs = [
    attr('service.name', getEnv(DEFAULT_SERVICE_KEY) || 'appero-web'),
    attr('deployment.environment', getEnv(DEFAULT_ENV_KEY) || 'production'),
    attr('telemetry.sdk.language', 'webjs'),
    attr('telemetry.sdk.name', 'appero-crash-reporter'),
  ].filter(Boolean)

  const spanAttrs = [
    attr('error', 'true'),
    attr('error.source', source),
    attr('exception.type', name),
    attr('exception.message', message),
    attr('exception.detail', error && error.detail),
    attr('http.status_code', error && error.status != null ? error.status : undefined),
    attr('browser.user_agent', typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    attr('http.url', typeof window !== 'undefined' && window.location ? window.location.href : ''),
  ].filter(Boolean)

  const eventAttrs = [
    attr('exception.type', name),
    attr('exception.message', message),
    attr('exception.stacktrace', stack),
    attr('exception.escaped', 'true'),
  ].filter(Boolean)

  return {
    resourceSpans: [
      {
        resource: { attributes: resourceAttrs },
        scopeSpans: [
          {
            scope: { name: 'appero-crash-reporter', version: '1.0.0' },
            spans: [
              {
                traceId: randomHex(16),
                spanId: randomHex(8),
                parentSpanId: '',
                name: `exception.${source}`,
                kind: 1,
                startTimeUnixNano: startNano,
                endTimeUnixNano: endNano,
                status: { code: 2, message: message.slice(0, 500) },
                attributes: spanAttrs,
                events: [
                  {
                    timeUnixNano: startNano,
                    name: 'exception',
                    attributes: eventAttrs,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  }
}

const sendPayload = (payload) => {
  const base = `${getEnv(DEFAULT_ENDPOINT_KEY) || ''}`.replace(/\/$/, '')
  if (!base) {
    return Promise.resolve(false)
  }
  const url = `${base}/v1/traces`
  const body = JSON.stringify(payload)

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    mode: 'cors',
  })
    .then((res) => res.ok)
    .catch(() => false)
}

export const reportError = (error, { source = 'manual', componentStack } = {}) => {
  if (!isEnabled()) {
    return Promise.resolve(false)
  }
  try {
    return sendPayload(buildPayload({ error, source, componentStack }))
  } catch (_) {
    return Promise.resolve(false)
  }
}

let installed = false

export const initCrashReporter = () => {
  if (installed || typeof window === 'undefined') {
    return
  }
  installed = true

  // Always expose a manual test hook (works even when auto-capture is off).
  window.__apperoReportCrash = (message = 'signoz crash test') => {
    const err = message instanceof Error ? message : new Error(String(message))
    return reportError(err, { source: 'manual-test' }).then((ok) => {
      // eslint-disable-next-line no-console
      console.info('[appero-crash-reporter]', ok ? 'sent' : 'not sent (check REACT_APP_OTEL_*)', {
        endpoint: getEnv(DEFAULT_ENDPOINT_KEY) || null,
        enabled: isEnabled(),
        service: getEnv(DEFAULT_SERVICE_KEY) || null,
      })
      return ok
    })
  }

  if (!isEnabled()) {
    // eslint-disable-next-line no-console
    console.info('[appero-crash-reporter] disabled — set REACT_APP_OTEL_ENDPOINT in env-config.js')
    return
  }

  window.addEventListener('error', (event) => {
    const err = event.error || new Error(event.message || 'window.onerror')
    reportError(err, { source: 'window.onerror' })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(normalizeRejectionReason(event.reason), { source: 'unhandledrejection' })
  })
}
